import React, { useMemo } from 'react';
import { StyleSheet, Pressable } from 'react-native';
import Text from '@/components/Typography/Text';
import { useModalStore } from '@/store';
import ChatInputBanner from '@/components/ChatInput/ChatInputBanner';
import useResponsiveness from '@/hooks/useResponsiveness';
import {
  trackConversionEvent,
  setCheckoutSourceDimension,
  CONVERSION_ACTION,
} from '@/constants/utils/analyticsEvents';
import useTranslation from '@/hooks/useTranslation';

const BANNER_SURFACE = 'chat_input';

const styles = StyleSheet.create({
  link: {
    marginLeft: 4,
    display: 'inline',
    textDecorationLine: 'underline',
  },
});

function ChatInputUpgradeBanner({ level, isCloseable = false, isDetached = false }) {
  const { openModal } = useModalStore(state => state.actions);
  const { isXS } = useResponsiveness();
  const { t } = useTranslation();

  // Winner of upgrade_banner_copy was "control" (random rotation from
  // existing copy set). The experiment is concluded — see
  // backend/scripts/seed/seed_real_experiments.json.
  const ctaTitles = useMemo(() => ({
    isXS: t('chatInput.upgradeBanner.isXS'),
    default: t('chatInput.upgradeBanner.default'),
  }), [t]);

  const titleRef = React.useRef(null);
  if (titleRef.current === null) {
    const variants = ctaTitles[isXS ? 'isXS' : 'default'];
    const idx = Math.floor(Math.random() * variants.length);
    titleRef.current = variants[idx];
  }
  const title = titleRef.current;
  const linkText = t('chatInput.upgradeBanner.upgradeLink');

  // The click was already tracked; without the matching impression there is no
  // denominator, so the banner's click-through rate can't be computed and one
  // placement can't be compared against another.
  React.useEffect(() => {
    trackConversionEvent(CONVERSION_ACTION.UPGRADE_BANNER_SHOWN, BANNER_SURFACE);
  }, []);

  const handleUpgradePress = React.useCallback(() => {
    trackConversionEvent(CONVERSION_ACTION.UPGRADE_BANNER_CLICKED, BANNER_SURFACE);
    setCheckoutSourceDimension(BANNER_SURFACE);
    openModal('PricingModal');
  }, [openModal]);

  return (
    <ChatInputBanner color="upgrade" level={level} isCloseable={isCloseable} isDetached={isDetached}>
      {title}
      <Pressable onPress={handleUpgradePress}>
        <Text size='s' weight='medium' color='white' style={styles.link}>
          {linkText}
        </Text>
      </Pressable>
    </ChatInputBanner>
  );
}

export default React.memo(ChatInputUpgradeBanner); 